// types/messages.ts
import { WebsiteMessage } from '../services/websiteMessageService';

/**
 * Extended message interface with simplified status
 * Requirements: 2.1, 2.2, 7.1, 7.2
 */
export interface ExtendedWebsiteMessage extends WebsiteMessage {
  // Simplified status information - only dismissed/active
  isDismissed: boolean;
  dismissedAt?: string;
  isBookmarked?: boolean;

  // Engagement data (for tracking purposes only)
  viewedAt?: string;
  clickedAt?: string;
  engagementCount: {
    views: number;
    clicks: number;
  };

  // Message source
  messageSource: 'global' | 'project_release' | 'task_related' | 'survey_related' | 'team_related';
  projectId?: string;
  projectTitle?: string;
  taskId?: string;
  taskTitle?: string;
  questionnaireId?: string;
  questionnaireTitle?: string;

  // Display helpers
  displayTimestamp: string;
  timeAgo: string;
  canUndismiss: boolean;
  isActive: boolean; // Computed property: !isDismissed && currently within date range
  metadata?: any; // Additional metadata for specific message types (e.g., contactMessageId for contact messages)
}

/**
 * Filter interface for message filtering (email-style)
 * Requirements: 6.1, 6.2
 */
export interface MessageFilters {
  readStatus: 'all' | 'unread' | 'read';
  type: 'all' | 'info' | 'welcome' | 'announcement' | 'maintenance' | 'project_release' | 'task_pending_review' | 'task_approved' | 'task_rejected' | 'survey_completed' | 'team_application_received' | 'team_application_approved' | 'team_application_rejected' | 'team_formed' | 'contact_form' | 'contact_reply';
  source: 'all' | 'global' | 'project_release' | 'task_related' | 'survey_related' | 'team_related';
  bookmarked?: boolean;
  dateRange: {
    start?: string;
    end?: string;
  };
}

/**
 * Message statistics interface (email-style)
 * Requirements: 6.1, 6.2
 */
export interface MessageStats {
  total: number;
  unread: number;
  read: number;
  bookmarked: number;
  byType: Record<string, number>;
  bySource: Record<string, number>;
}

/**
 * Bulk action interface (email-style)
 * Requirements: 4.1, 4.2
 */
export interface BulkAction {
  type: 'mark_read' | 'mark_unread' | 'bookmark' | 'unbookmark' | 'delete';
  messageIds: string[];
}

/**
 * Message action types for individual message interactions (email-style)
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5
 */
export type MessageAction =
  | 'mark_read'
  | 'mark_unread'
  | 'bookmark'
  | 'unbookmark'
  | 'click_link'
  | 'action_button_click';

/**
 * Pagination state interface
 * Requirements: 1.2
 */
export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

/**
 * Message interaction event interface
 * Requirements: 8.3, 8.5
 */
export interface MessageInteractionEvent {
  messageId: string;
  userId: string;
  interactionType: 'view' | 'click' | 'dismiss' | 'bookmark' | 'link_click' | 'action_button_click';
  timestamp: string;
  metadata?: {
    url?: string;
    source?: 'top_bar' | 'messages_page';
    bulk_action?: boolean;
    link_type?: 'internal' | 'external';
    action_type?: string;
    [key: string]: any;
  };
}

/**
 * Link information extracted from message content
 * Requirements: 8.2, 8.5
 */
export interface MessageLink {
  url: string;
  text: string;
  isExternal: boolean;
  position: {
    start: number;
    end: number;
  };
}

/**
 * Action button configuration for messages
 * Requirements: 8.3
 */
export interface MessageActionButton {
  id: string;
  text: string;
  url?: string;
  action?: string;
  style?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}

/**
 * Message display state interface
 * Requirements: 7.1, 7.2
 */
export interface MessageDisplayState {
  expanded: boolean;
  selected: boolean;
  highlighted: boolean;
}

/**
 * Search result interface
 * Requirements: 5.1, 5.2, 5.3
 */
export interface MessageSearchResult {
  message: ExtendedWebsiteMessage;
  matchedFields: string[];
  highlightedContent: string;
  relevanceScore: number;
}

/**
 * Filter validation result interface
 * Requirements: 6.1, 6.2
 */
export interface FilterValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Helper Functions for Message State Management and Display Formatting

/**
 * Helper functions for message state management
 * Requirements: 2.1, 2.2, 7.1, 7.2
 */
export class MessageStateHelpers {
  /**
   * Check if a message can be undismissed
   * Requirements: 2.2, 3.4
   */
  static canUndismissMessage(message: ExtendedWebsiteMessage): boolean {
    if (!message.isDismissed) return false;

    // Check if message is still within its active period
    const now = new Date();
    const endDate = message.end_date ? new Date(message.end_date) : null;

    return message.is_active && (!endDate || endDate > now);
  }

  /**
   * Check if a message is currently active and should be displayed
   * Requirements: 2.1, 2.2
   */
  static isMessageActive(message: WebsiteMessage): boolean {
    const now = new Date();
    const startDate = new Date(message.start_date);
    const endDate = message.end_date ? new Date(message.end_date) : null;

    return message.is_active &&
      startDate <= now &&
      (!endDate || endDate > now);
  }

  /**
   * Get message status for display
   * Requirements: 2.1, 2.2, 7.1
   */
  static getMessageStatus(message: ExtendedWebsiteMessage): {
    status: 'active' | 'dismissed' | 'expired' | 'scheduled';
    label: string;
    className: string;
  } {
    const now = new Date();
    const startDate = new Date(message.start_date);
    const endDate = message.end_date ? new Date(message.end_date) : null;

    if (message.isDismissed) {
      return {
        status: 'dismissed',
        label: 'Dismissed',
        className: 'message-status-dismissed'
      };
    }

    if (!message.is_active || (endDate && endDate < now)) {
      return {
        status: 'expired',
        label: 'Expired',
        className: 'message-status-expired'
      };
    }

    if (startDate > now) {
      return {
        status: 'scheduled',
        label: 'Scheduled',
        className: 'message-status-scheduled'
      };
    }

    return {
      status: 'active',
      label: 'Active',
      className: 'message-status-active'
    };
  }

  /**
   * Calculate message statistics from a list of messages (email-style)
   * Requirements: 6.1, 6.2
   */
  static calculateMessageStats(messages: ExtendedWebsiteMessage[]): MessageStats {
    const stats: MessageStats = {
      total: messages.length,
      unread: 0,
      read: 0,
      bookmarked: 0,
      byType: {},
      bySource: {}
    };

    messages.forEach(message => {
      // Read/unread counts
      // Note: A message is considered "read" if it has been viewed OR dismissed
      // This ensures dismissed messages (e.g., survey completed, team applications) are counted as read
      if (message.viewedAt || message.isDismissed) {
        stats.read++;
      } else {
        stats.unread++;
      }

      // Bookmarked count
      if (message.isBookmarked) {
        stats.bookmarked++;
      }

      // Type counts
      const type = message.message_type;
      stats.byType[type] = (stats.byType[type] || 0) + 1;

      // Source counts
      const source = message.messageSource;
      stats.bySource[source] = (stats.bySource[source] || 0) + 1;
    });

    return stats;
  }

  /**
   * Validate message filters (email-style)
   * Requirements: 6.1, 6.2
   */
  static validateFilters(filters: MessageFilters): FilterValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate date range
    if (filters.dateRange.start && filters.dateRange.end) {
      const startDate = new Date(filters.dateRange.start);
      const endDate = new Date(filters.dateRange.end);

      if (startDate > endDate) {
        errors.push('Start date cannot be after end date');
      }

      if (endDate > new Date()) {
        warnings.push('End date is in the future');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }
}

/**
 * Helper functions for message display formatting
 * Requirements: 7.1, 7.2
 */
export class MessageDisplayHelpers {
  /**
   * Format timestamp for display
   * Requirements: 7.2
   */
  static formatDisplayTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const minutes = Math.floor(diffInHours * 60);
      return minutes <= 1 ? 'Just now' : `${minutes} minutes ago`;
    }

    if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    }

    if (diffInHours < 168) { // 7 days
      const days = Math.floor(diffInHours / 24);
      return days === 1 ? '1 day ago' : `${days} days ago`;
    }

    // For older messages, show the actual date
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Get time ago string for display
   * Requirements: 7.2
   */
  static getTimeAgo(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return diffInMinutes === 1 ? '1 minute ago' : `${diffInMinutes} minutes ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return diffInMonths === 1 ? '1 month ago' : `${diffInMonths} months ago`;
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return diffInYears === 1 ? '1 year ago' : `${diffInYears} years ago`;
  }

  /**
   * Get message type icon and styling
   * Requirements: 7.1
   */
  static getMessageTypeDisplay(messageType: string): {
    icon: string;
    className: string;
    label: string;
  } {
    const typeMap = {
      info: {
        icon: 'ℹ️',
        className: 'message-type-info',
        label: 'Information'
      },
      welcome: {
        icon: '👋',
        className: 'message-type-welcome',
        label: 'Welcome'
      },
      announcement: {
        icon: '📢',
        className: 'message-type-announcement',
        label: 'Announcement'
      },
      maintenance: {
        icon: '🔧',
        className: 'message-type-maintenance',
        label: 'Maintenance'
      },
      task_pending_review: {
        icon: '⏳',
        className: 'message-type-task-pending',
        label: 'Task Pending Review'
      },
      task_approved: {
        icon: '✅',
        className: 'message-type-task-approved',
        label: 'Task Approved'
      },
      task_rejected: {
        icon: '❌',
        className: 'message-type-task-rejected',
        label: 'Task Rejected'
      },
      survey_completed: {
        icon: '🎉',
        className: 'message-type-survey-completed',
        label: 'Survey Completed'
      },
      team_application_received: {
        icon: '📋',
        className: 'message-type-team-application',
        label: 'New Team Application'
      },
      team_application_approved: {
        icon: '🎉',
        className: 'message-type-team-approved',
        label: 'Application Approved'
      },
      team_application_rejected: {
        icon: '❌',
        className: 'message-type-team-rejected',
        label: 'Application Rejected'
      },
      team_formed: {
        icon: '🚀',
        className: 'message-type-team-formed',
        label: 'Team Formed'
      },
      contact_form: {
        icon: '📬',
        className: 'message-type-contact-form',
        label: 'Contact Form Submission'
      },
      contact_reply: {
        icon: '💬',
        className: 'message-type-contact-reply',
        label: 'Contact Reply'
      }

    };

    return typeMap[messageType as keyof typeof typeMap] || {
      icon: '📝',
      className: 'message-type-default',
      label: 'Message'
    };
  }

  /**
   * Get message source display information
   * Requirements: 7.1
   */
  static getMessageSourceDisplay(messageSource: string, projectTitle?: string, taskTitle?: string, questionnaireTitle?: string): {
    label: string;
    className: string;
    fullLabel: string;
  } {
    if (messageSource === 'project_release') {
      return {
        label: 'Project',
        className: 'message-source-project',
        fullLabel: projectTitle ? `Project: ${projectTitle}` : 'Project Release'
      };
    }

    if (messageSource === 'task_related') {
      return {
        label: 'Task',
        className: 'message-source-task',
        fullLabel: taskTitle ? `Task: ${taskTitle}` : 'Task Related'
      };
    }

    if (messageSource === 'survey_related') {
      return {
        label: 'Survey',
        className: 'message-source-survey',
        fullLabel: questionnaireTitle ? `Survey: ${questionnaireTitle}` : 'Survey Related'
      };
    }

    return {
      label: 'Global',
      className: 'message-source-global',
      fullLabel: 'Global Message'
    };
  }

  /**
   * Truncate message content for list display
   * Requirements: 7.2
   */
  static truncateContent(content: string, maxLength: number = 150): {
    truncated: string;
    isTruncated: boolean;
  } {
    if (content.length <= maxLength) {
      return {
        truncated: content,
        isTruncated: false
      };
    }

    // Find the last space before the max length to avoid cutting words
    let truncateAt = maxLength;
    for (let i = maxLength; i >= 0; i--) {
      if (content[i] === ' ') {
        truncateAt = i;
        break;
      }
    }

    return {
      truncated: content.substring(0, truncateAt) + '...',
      isTruncated: true
    };
  }

  /**
   * Highlight search terms in message content
   * Requirements: 5.4
   */
  static highlightSearchTerms(content: string, searchQuery: string): string {
    if (!searchQuery.trim()) {
      return content;
    }

    const terms = searchQuery.trim().split(/\s+/);
    let highlightedContent = content;

    terms.forEach(term => {
      const regex = new RegExp(`(${term})`, 'gi');
      highlightedContent = highlightedContent.replace(
        regex,
        '<mark class="search-highlight">$1</mark>'
      );
    });

    return highlightedContent;
  }

  /**
   * Extract links from message content
   * Requirements: 8.2, 8.5
   */
  static extractLinksFromContent(content: string): MessageLink[] {
    const links: MessageLink[] = [];

    // URL regex pattern that matches http/https URLs
    const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;
    let match;

    while ((match = urlRegex.exec(content)) !== null) {
      const url = match[0];
      const isExternal = !url.includes(window.location.hostname);

      links.push({
        url,
        text: url,
        isExternal,
        position: {
          start: match.index,
          end: match.index + url.length
        }
      });
    }

    return links;
  }

  /**
   * Process markdown-style links in task messages and rewrite submission URLs to task URLs
   * Requirements: 8.2, 8.5
   */
  static processTaskMessageLinks(content: string, messageType: string): string {
    // For task submission notifications, rewrite the URL to point to the task instead of submission
    if (messageType === 'task_pending_review') {
      // Replace [View Submission](/tasks/{task_id}/submissions/{submission_id}) with [View Task](/tasks/{task_id})
      content = content.replace(
        /\[View Submission\]\(\/tasks\/([^/]+)\/submissions\/[^)]+\)/g,
        '[View Task](/tasks/$1)'
      );
    }

    // Convert markdown-style links to HTML links
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    return content.replace(linkRegex, (match, linkText, url) => {
      const isExternal = !url.startsWith('/') && !url.includes(window.location.hostname);
      const target = isExternal || url.includes('/tasks/') ? '_blank' : '_self';
      const rel = isExternal || url.includes('/tasks/') ? 'noopener noreferrer' : '';

      return `<a href="${url}" target="${target}" rel="${rel}" class="message-link ${isExternal ? 'external-link' : 'internal-link'}" data-url="${url}" data-external="${isExternal}">${linkText}</a>`;
    });
  }

  /**
   * Process team message links - convert markdown to HTML
   * Requirements: 8.2, 8.5
   */
  static processTeamMessageLinks(content: string): string {
    // Convert markdown-style links to HTML links
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    return content.replace(linkRegex, (match, linkText, url) => {
      const isExternal = !url.startsWith('/') && !url.includes(window.location.hostname);
      // Team links should open in same tab for better UX
      const target = isExternal ? '_blank' : '_self';
      const rel = isExternal ? 'noopener noreferrer' : '';

      return `<a href="${url}" target="${target}" rel="${rel}" class="message-link ${isExternal ? 'external-link' : 'internal-link'}" data-url="${url}" data-external="${isExternal}">${linkText}</a>`;
    });
  }

  /**
   * Make links in content clickable with proper tracking
   * Requirements: 8.2, 8.5
   */
  static makeLinksClickable(
    content: string,
    onLinkClick: (url: string, isExternal: boolean) => void
  ): string {
    const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;

    return content.replace(urlRegex, (url) => {
      const isExternal = !url.includes(window.location.hostname);
      const target = isExternal ? '_blank' : '_self';
      const rel = isExternal ? 'noopener noreferrer' : '';

      return `<a href="${url}" target="${target}" rel="${rel}" class="message-link ${isExternal ? 'external-link' : 'internal-link'}" data-url="${url}" data-external="${isExternal}">${url}</a>`;
    });
  }

  /**
   * Parse action buttons from message content
   * Requirements: 8.3
   */
  static parseActionButtons(content: string): MessageActionButton[] {
    const buttons: MessageActionButton[] = [];

    // Look for action button patterns in content like [Button Text](action:type) or [Button Text](url)
    const buttonRegex = /\[([^\]]+)\]\(((?:action:|https?:\/\/)[^)]+)\)/gi;
    let match;

    while ((match = buttonRegex.exec(content)) !== null) {
      const text = match[1];
      const target = match[2];

      if (target.startsWith('action:')) {
        // Action button
        const action = target.substring(7); // Remove 'action:' prefix
        buttons.push({
          id: `action_${buttons.length}`,
          text,
          action,
          style: 'primary'
        });
      } else if (target.startsWith('http')) {
        // URL button
        buttons.push({
          id: `url_${buttons.length}`,
          text,
          url: target,
          style: 'secondary'
        });
      }
    }

    return buttons;
  }

  /**
   * Remove action button markup from content for display
   * Requirements: 8.3
   */
  static removeActionButtonMarkup(content: string): string {
    // Remove action button patterns
    return content.replace(/\[([^\]]+)\]\(((?:action:|https?:\/\/)[^)]+)\)/gi, '');
  }

  /**
   * Create extended message from base message with default values
   * Requirements: 2.1, 2.2
   */
  static createExtendedMessage(
    baseMessage: WebsiteMessage,
    engagementData?: {
      isRead?: boolean;
      isDismissed?: boolean;
      dismissedAt?: string;
      isBookmarked?: boolean;
      viewedAt?: string;
      clickedAt?: string;
      views?: number;
      clicks?: number;
    },
    sourceData?: {
      messageSource?: 'global' | 'project_release' | 'task_related' | 'survey_related' | 'team_related';
      projectId?: string;
      projectTitle?: string;
      taskId?: string;
      taskTitle?: string;
      questionnaireId?: string;
      questionnaireTitle?: string;
    }
  ): ExtendedWebsiteMessage {
    const extended: ExtendedWebsiteMessage = {
      ...baseMessage,
      // Simplified status information - only dismissed
      isDismissed: engagementData?.isDismissed ?? false,
      dismissedAt: engagementData?.dismissedAt,
      isBookmarked: engagementData?.isBookmarked ?? false,

      // Engagement data (for tracking purposes only)
      viewedAt: engagementData?.viewedAt,
      clickedAt: engagementData?.clickedAt,
      engagementCount: {
        views: engagementData?.views ?? 0,
        clicks: engagementData?.clicks ?? 0
      },

      // Message source
      messageSource: sourceData?.messageSource ?? 'global',
      projectId: sourceData?.projectId,
      projectTitle: sourceData?.projectTitle,
      taskId: sourceData?.taskId,
      taskTitle: sourceData?.taskTitle,
      questionnaireId: sourceData?.questionnaireId,
      questionnaireTitle: sourceData?.questionnaireTitle,

      // Display helpers (computed)
      displayTimestamp: '',
      timeAgo: '',
      canUndismiss: false,
      isActive: false // Will be computed below
    };

    // Compute display helpers
    extended.displayTimestamp = this.formatDisplayTimestamp(baseMessage.created_at);
    extended.timeAgo = this.getTimeAgo(baseMessage.created_at);
    extended.canUndismiss = MessageStateHelpers.canUndismissMessage(extended);
    extended.isActive = MessageStateHelpers.isMessageActive(extended);

    return extended;
  }
}